import { z } from 'zod'

import {
  TriggerEventType,
  TriggerRuleFrequencyType,
  TriggerRuleStatusType,
} from 'apps/triggerRules/models/TriggerRule/constants'
import {
  TriggerRuleSchema,
  TriggerRuleTriggerSchema,
  TriggerRuleIdSchema,
} from 'shared/api/common/schemas/flow/triggerRule'

const CreateRuleBodySchema = z.object({
  ns: z.string().optional(),
  title: z.string().optional(),
  triggers: z.array(TriggerRuleTriggerSchema).nullable(),
  type: z.string().optional(),
  draft: z
    .object({
      initial_trigger_event_type: z.nativeEnum(TriggerEventType),
    })
    .nullable(),
  frequency_settings: z.nativeEnum(TriggerRuleFrequencyType),
})

export type CreateRuleBody = z.infer<typeof CreateRuleBodySchema>

export const CreateTriggerRuleSchema = {
  path: z.undefined(),
  request: CreateRuleBodySchema,
  response: z.object({
    trigger_rule: TriggerRuleSchema,
  }),
  query: z.undefined(),
}

export const SetTitleSchema = {
  path: z.undefined(),
  request: z.object({
    title: z.string(),
    rule_id: TriggerRuleIdSchema,
  }),
  response: z.object({
    trigger_rule: TriggerRuleSchema,
  }),
  query: z.undefined(),
}

export const DeleteTriggerRuleSchema = {
  path: z.undefined(),
  request: z.object({
    rule_id: TriggerRuleIdSchema,
  }),
  response: z.undefined(),
  query: z.undefined(),
}

export const ListTriggerRulesSchema = {
  path: z.undefined(),
  request: z.undefined(),
  response: z.object({
    trigger_rules: z.array(TriggerRuleSchema),
  }),
  query: z.undefined(),
}

export const ChangeStatusSchema = {
  path: z.undefined(),
  request: z.object({
    rule_id: TriggerRuleIdSchema,
    status: z.nativeEnum(TriggerRuleStatusType),
    async: z.string().optional(),
  }),
  response: z.object({
    trigger_rule: TriggerRuleSchema.optional(),
    async: z.string().optional(),
  }),
  query: z.undefined(),
}

export const RestoreTriggerRuleSchema = {
  path: z.undefined(),
  request: z.object({
    rule_id: TriggerRuleIdSchema,
  }),
  response: z.object({
    trigger_rule: TriggerRuleSchema,
  }),
  query: z.undefined(),
}

export const UpdateTriggerRuleSchema = {
  path: z.undefined(),
  request: z.object({
    trigger_rule: TriggerRuleSchema.partial(),
    rule_id: TriggerRuleIdSchema,
  }),
  response: z.object({
    trigger_rule: TriggerRuleSchema,
    need_activate: z.boolean(),
  }),
  query: z.undefined(),
}

export const CopyTriggerRuleSchema = {
  path: z.undefined(),
  request: z.object({
    rule_id: TriggerRuleIdSchema,
  }),
  response: z.object({
    trigger_rule: TriggerRuleSchema,
  }),
  query: z.undefined(),
}

export const GetTriggerRuleSchema = {
  path: z.undefined(),
  response: z.object({
    trigger_rule: TriggerRuleSchema,
    template_installation_access_data: z
      .object({
        has_user_access: z.boolean(),
        is_locked: z.boolean(),
        is_protected: z.boolean(),
        is_disabled: z.boolean(),
        is_owner: z.boolean(),
      })
      .optional(),
  }),
  query: z.object({
    rule_id: TriggerRuleIdSchema,
  }),
}
